import { globalRef } from '@amazon/vinyl-util'
import { ctx } from '../env/playbackContext'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import { PLAYBACK_STORAGE_KEYS } from '../env/playbackStorageKeys'
import type { PlaylistEntry } from './PlaylistController'
import { isAudiobookEntry, isPodcastEntry } from './playbackEntryKind'

/** Spoken-content speed profile. Each kind remembers its own speed; every other entry plays at 1x. */
export type PlaybackRateKind = 'general' | 'audiobook'

const SPOKEN_CONTENT_RATES: readonly number[] = [0.5, 0.8, 1, 1.2, 1.4, 1.5, 1.6, 1.8, 2, 3]

export const PLAYBACK_RATE_PRESETS: Record<PlaybackRateKind, readonly number[]> = {
    general: SPOKEN_CONTENT_RATES,
    audiobook: SPOKEN_CONTENT_RATES,
}

const DEFAULT_RATE = 1

const STORAGE_KEYS: Record<PlaybackRateKind, string> = {
    general: PLAYBACK_STORAGE_KEYS.playbackRate,
    audiobook: PLAYBACK_STORAGE_KEYS.audiobookPlaybackRate,
}

const { errorHandler } = ctx('playbackRatePreference')

function readStoredRate(kind: PlaybackRateKind): number {
    try {
        const raw = getPlaybackEnv().store.getItem(STORAGE_KEYS[kind])
        if (raw == null) return DEFAULT_RATE
        const rate: unknown = JSON.parse(raw)
        return typeof rate === 'number' && isPlaybackRatePreset(kind, rate) ? rate : DEFAULT_RATE
    } catch {
        return DEFAULT_RATE
    }
}

// The feeder applies a rate on the same tick an entry becomes current, so the store is read
// synchronously, once, on first use.
const remembered = globalRef<Record<PlaybackRateKind, number>>(() => ({
    general: readStoredRate('general'),
    audiobook: readStoredRate('audiobook'),
}))

/** The next read takes the speeds from the store again. */
export function resetRememberedPlaybackRates(): void {
    remembered.reset()
}

export function playbackRateKind(entry: PlaylistEntry | null): PlaybackRateKind | null {
    if (isAudiobookEntry(entry)) return 'audiobook'
    return isPodcastEntry(entry) ? 'general' : null
}

export function isPlaybackRatePreset(kind: PlaybackRateKind, rate: number): boolean {
    return (
        Object.keys(PLAYBACK_RATE_PRESETS).includes(kind) &&
        PLAYBACK_RATE_PRESETS[kind].includes(rate)
    )
}

export function writeStoredPlaybackRate(kind: PlaybackRateKind, rate: number): void {
    remembered.value[kind] = rate
    try {
        getPlaybackEnv().store.setItem(STORAGE_KEYS[kind], JSON.stringify(rate))
    } catch (error) {
        errorHandler(error)
    }
}

/**
 * The rate `entry` should play at: its kind's remembered speed, else 1x. A
 * stored speed outside that kind's presets falls back to 1x.
 */
export function intendedPlaybackRate(entry: PlaylistEntry): number {
    const kind = playbackRateKind(entry)
    return kind ? remembered.value[kind] : DEFAULT_RATE
}

/** Where a picked speed is remembered: in the runtime that feeds the entries. */
export interface PlaybackRatePreference {
    remember(kind: PlaybackRateKind, rate: number): void
}

export const localPlaybackRatePreference: PlaybackRatePreference = {
    remember: writeStoredPlaybackRate,
}
